// ProjectList.js

import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Tabs,
  Tab,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  TablePagination,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon,
  CurrencyRupee as RupeeIcon,
  ViewModule as ModuleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getProjectsPaginated, deleteProject, flattenProject } from './../../../services/project';
import AlertModal from '../../../components/AlertModal';
import ConfirmModal from '../../../components/ConfirmModal';
import { hasReadPermission, hasWritePermission } from '../../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import { exportAsExcel, exportAsCSV, exportAsPDF } from '../../../utils/importExport';
import ExportButton from '../../../components/ExportButton/ExportButton';
import RefreshButton from '../../../components/RefreshButton/RefreshButton';
import AvatarMui from '@mui/material/Avatar';

const typeInventoryConfig = {
  plot: { key: 'totalPlot', label: 'Total Plots' },
  apartment: { key: 'totalBuilding', label: 'Total Buildings' },
  rowhouse: { key: 'totalRowHouses', label: 'Total Row Houses' },
  villa: { key: 'totalVillas', label: 'Total Villas' },
  commercial: { key: 'totalUnits', label: 'Total Units' },
  residential: { key: 'totalUnits', label: 'Total Units' },
};

// Reset Button Component
const ResetButton = ({ onClick, disabled }) => (
  <Button
    variant="outlined"
    color="error"
    startIcon={<RefreshIcon />}
    onClick={onClick}
    disabled={disabled}
    fullWidth
    sx={{
      height: '56px',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '12px',
      borderRadius: 2,
      borderColor: '#d32f2f',
      '&:hover': {
        borderColor: '#b71c1c',
        backgroundColor: 'rgba(211, 47, 47, 0.04)',
      },
    }}
  >
    Reset
  </Button>
);

const ProjectList = () => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State management
  const [projects, setProjects] = useState([]);
  const [selectedTab, setSelectedTab] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [tabCounts, setTabCounts] = useState({});
  const [globalTabCounts, setGlobalTabCounts] = useState({});
  const [globalTotalCount, setGlobalTotalCount] = useState(0);

  const canReadProject = hasReadPermission('project.project-list') || hasReadPermission('project');
  const canWriteProject = hasWritePermission('project.project-list') || hasWritePermission('project');
  const [canManageProjects, setCanManageProjects] = useState(canWriteProject);

  // Modal states
  const [alertConfig, setAlertConfig] = useState({ open: false, title: 'Alert', message: '', type: 'info' });
  const [confirmConfig, setConfirmConfig] = useState({ open: false, projectId: null });

  const showAlert = (message, type = 'info', title = 'Alert') => {
    setAlertConfig({ open: true, title, message, type });
  };

  const showConfirm = (projectId) => {
    setConfirmConfig({ open: true, projectId });
  };

  useEffect(() => {
    fetchProjects();
  }, [page, rowsPerPage, tabValue, searchQuery]);

  useEffect(() => {
    setCanManageProjects(canWriteProject);
  }, [canWriteProject]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery
      };

      if (tabValue === 1) params.projectType = ['Apartment', 'Plot', 'RowHouse', 'Residential'];
      if (tabValue === 2) params.projectType = 'Commercial';
      if (tabValue === 3) params.projectType = 'Villa';

      const response = await getProjectsPaginated(params);
      // API returns { data: [...], extra: { total: 28, ... } }
      const projectsData = response.data || [];
      const extra = response.extra || {};

      setProjects(projectsData.map(flattenProject));
      setTotalCount(extra.total || projectsData.length);
      setTabCounts(extra.projectTypeCount || {});
      if (tabValue === 0 && !searchQuery) {
        setGlobalTotalCount(extra.total || projectsData.length);
        setGlobalTabCounts(extra.projectTypeCount || {});
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      showAlert('Failed to load projects.', 'error', 'Error');
    } finally {
      setLoading(false);
    }
  };

  // Handle functions
  const handleTabChange = (tabId, newValue) => {
    if (selectedTab?.id === tabId && selectedTab?.value === newValue) {
      setSelectedTab(null);
    } else {
      setSelectedTab({ id: tabId, value: newValue });
    }
  };


  const getSelectedTab = (tabId) => {
    if (selectedTab === null) {
      return 0;
    }
    return selectedTab?.id === tabId ? selectedTab.value : 0;
  };


  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setShowSuggestions(Boolean(value.trim()));
  };


  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };


  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const applySearch = (value = searchInput) => {
    const term = value.trim();
    setSearchQuery(term);
    setPage(0);
    setShowSuggestions(false);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchQuery('');
    setTabValue(0);
    setPage(0);
  };

  const suggestionOptions = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) return [];

    return projects
      .filter((project) => {
        const nameMatch = project.name?.toLowerCase().includes(query);
        const idMatch = project.projectId?.toLowerCase().includes(query);
        const developerMatch = project.developer?.toLowerCase().includes(query);
        const cityMatch = project.city?.toLowerCase().includes(query);
        return nameMatch || idMatch || developerMatch || cityMatch;
      })
      .slice(0, 5);
  }, [projects, searchInput]);


  const handleAddNewProject = () => {
    if (!canManageProjects) return;
    history.push('/app/project/add');
  };


  const handleEdit = (project) => {
    console.log(project, 'sdjdjdjc');
    if (!canManageProjects) return;
    // Ensure progressType and commission are included in the project data
    const projectData = {
      ...project,
      progressType: project.progressType || '',
      commission: project.commission !== undefined && project.commission !== null ? project.commission : '',
    };
    history.push({
      pathname: '/app/project/edit',
      state: { project: projectData }
    });
  };


  const handleViewDetails = (project) => {
    history.push({
      pathname: '/app/project/details',
      state: { project: project }
    });
  };

  const handleDelete = (projectId) => {
    if (!canManageProjects) return;
    showConfirm(projectId);
  };

  const confirmDelete = async () => {
    const projectId = confirmConfig.projectId;
    setConfirmConfig({ ...confirmConfig, open: false });

    try {
      const response = await deleteProject(projectId);
      // Check if API returned status: false
      if (response && response.status === false) {
        showAlert(response.message || 'Error deleting project.', 'error', 'Error');
        return;
      }
      setProjects(projects.filter((p) => p._id !== projectId));
      showAlert('Project deleted successfully!', 'success', 'Success');
    } catch (error) {
      // Check for error response from Axios
      const errorMsg = error.response?.data?.message || error.message || 'Error deleting project.';
      showAlert(errorMsg, 'error', 'Error');
    }
  };


  const buildExportRows = (list) => {
    return list.map((p) => ({
      'Project ID': p.projectId || 'N/A',
      'Project Name': p.name || 'N/A',
      'Type': p.type || 'N/A',
      'Developer': p.developer || 'N/A',
      'City': p.city || 'N/A',
      'State': p.state || 'N/A',
      'RERA No': p.reraNo || 'N/A',
      'Start Date': p.startDate || 'N/A',
      'Complete Date': p.completeDate || 'N/A',
      'Status': p.status || 'N/A',
      'Contact Person': p.contactPerson || 'N/A',
      'Phone': p.phone || 'N/A',
      'Email': p.email || 'N/A',
      'Commission Method': p.commissionMethod || 'N/A',
      'Commission': p.commission || 'N/A',
      'Reward Points': p.rewardPoints || 'N/A'
    }));
  };

  const handleExportPdf = () => {
    const list = displayData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    if (!list.length) {
      showAlert('No project data available on this page to export.', 'info');
      return;
    }
    const rows = buildExportRows(list);
    exportAsPDF({ rows, title: 'Project List', fileNameBase: 'Projects_Export' });
  };

  const handleExportCsv = () => {
    const list = displayData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    if (!list.length) {
      showAlert('No project data available on this page to export.', 'info');
      return;
    }
    const rows = buildExportRows(list);
    exportAsCSV({ rows, fileNameBase: 'Projects_Export' });
  };

  const handleExportExcel = () => {
    const list = displayData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    if (!list.length) {
      showAlert('No project data available on this page to export.', 'info');
      return;
    }
    const rows = buildExportRows(list);
    exportAsExcel({ rows, sheetName: 'Projects', fileNameBase: 'Projects_Export' });
  };

  // Filter projects
  const filteredData = projects.filter(
    (project) =>
      !searchQuery ||
      (project?.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (project?.projectId?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (project?.developer?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (project?.city?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );


  const getFilteredByType = () => {
    if (tabValue === 0) return filteredData;
    if (tabValue === 1) return filteredData.filter((p) => ['residential', 'apartment', 'plot', 'rowhouse'].includes((p.type || '').toLowerCase()));
    if (tabValue === 2) return filteredData.filter((p) => (p.type || '').toLowerCase() === 'commercial');
    if (tabValue === 3) return filteredData.filter((p) => (p.type || '').toLowerCase() === 'villa');
    return filteredData;
  };


  // Data is now fetched server-side per page
  const displayData = getFilteredByType();
  const residentialTotal = (globalTabCounts.Apartment || 0) + (globalTabCounts.Plot || 0) + (globalTabCounts.RowHouse || 0) + (globalTabCounts.Residential || 0);
  const commercialTotal = globalTabCounts.Commercial || 0;
  const villaTotal = globalTabCounts.Villa || 0;


  if (!canReadProject) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view the Project List. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Project Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all project listings
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            {canManageProjects && (
              <ExportButton
                onExportPdf={handleExportPdf}
                onExportCsv={handleExportCsv}
                onExportExcel={handleExportExcel}
                variant="outlined"
                sx={{
                  px: 2.5,
                  py: 1.2,
                  fontSize: '15px',
                  width: { xs: '100%', sm: 'auto' },
                  height: { xs: 'auto', sm: 44 }
                }}
              />
            )}
            {canManageProjects && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddNewProject}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                  textTransform: 'none',
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2,
                  height: { xs: 'auto', sm: 44 }
                }}
              >
                Add New Project
              </Button>
            )}
            <RefreshButton
              onClick={fetchProjects}
              loading={loading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 44 }
              }}
            />
          </Box>
        </Box>
        {!canManageProjects && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {/* You do not have permission to add or edit projects. Please contact an administrator for access. */}
          </Typography>
        )}
      </Box>


      {/* Search Bar */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, overflow: 'visible' }}>
        <CardContent sx={{ position: 'relative', overflow: 'visible' }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={8} md={9}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  placeholder="Search by project ID, name, developer, or city"
                  value={searchInput}
                  onChange={handleSearchChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      applySearch();
                    }
                  }}
                  onFocus={() => {
                    if (searchInput.trim() && suggestionOptions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 120);
                  }}
                />
                {showSuggestions && suggestionOptions.length > 0 && (
                  <Paper
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 1,
                      zIndex: 10,
                      maxHeight: 240,
                      overflowY: 'auto',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                  >
                    {suggestionOptions.map((project) => (
                      <Box
                        key={project.projectId}
                        sx={{
                          px: 2,
                          py: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(83, 109, 254, 0.08)' },
                        }}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setSearchInput(project.name);
                          applySearch(project.name);
                          setShowSuggestions(false);
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {project.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {project.projectId}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {project.city}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={2} md={1.5}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={() => applySearch()}
                sx={{
                  height: '56px',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} sm={2} md={1.5}>
              <ResetButton onClick={handleReset} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>


      {/* Filter Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          value={tabValue}
          onChange={(e, newValue) => {
            setTabValue(newValue);
            setPage(0);
          }}
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              minHeight: 56
            }
          }}
        >
          <Tab label={`All Projects (${globalTotalCount})`} />
          <Tab label={`Residential (${residentialTotal})`} />
          <Tab label={`Commercial (${commercialTotal})`} />
          <Tab label={`Villa (${villaTotal})`} />
        </Tabs>
      </Box>


      {/* Projects Cards Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {loading ? (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
              <CircularProgress color="primary" />
            </Box>
          </Grid>
        ) : displayData.length === 0 ? (
          <Grid item xs={12}>
            <Box
              sx={{
                py: 10,
                px: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                backgroundColor: 'white',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                No projects found
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Try adjusting your search or filters to find what you're looking for.
              </Typography>
              <Button
                variant="outlined"
                onClick={handleReset}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              >
                Clear all filters
              </Button>
            </Box>
          </Grid>
        ) : displayData
          .map((project) => {
            const activeTab = getSelectedTab(project.projectId);

            return (
              <Grid item xs={12} key={project.projectId}>
                <Card
                  sx={{
                    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  {/* Card Header - Blue with 3 columns */}
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: '#536DFE',
                      color: 'white',
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', md: 'center' },
                      gap: { xs: 1, md: 2 }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                        Project NO:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '16px' }}>
                        {project.projectId}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                        Commission Method:
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '16px' }}>
                          {project?.commissionMethod === 'level'
                            ? 'Level'
                            : project?.commissionMethod === 'project'
                              ? `${project?.commission || 'N/A'}${project?.commission && project?.commission !== 'N/A' && !String(project?.commission).includes('%') ? '%' : ''}`
                              : 'N/A'
                          }
                          {/* {project?.commissionMethod || 'N/A'} */}
                        </Typography>
                      </Box>
                    </Box>


                    {/* <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                        Reward Points:
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '16px' }}>
                          {`${project?.rewardPoints || 'N/A'}`}
                        </Typography>
                      </Box>
                    </Box> */}


                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                        Status:
                      </Typography>
                      <Chip
                        label={project?.status || 'N/A'}
                        size="small"
                        sx={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '12px',
                          height: '24px'
                        }}
                      />
                    </Box>
                  </Box>


                  {/* Card Content */}
                  <CardContent sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      {/* Left Side: Project Info */}
                      <Grid item xs={12} md={5}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          {/* Badge + Icon */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={project?.projectType || project?.badge || 'N/A'}
                              sx={{
                                backgroundColor: project?.badgeColor,
                                color: '#000',
                                fontWeight: 700,
                                fontSize: '11px',
                                height: '22px',
                              }}
                            />
                            <Avatar
                              src={project?.projectLogoViewUrl || project?.logoViewUrl || undefined}
                              sx={{
                                width: 80,
                                height: 80,
                                backgroundColor: '#E3F2FD',
                                border: '3px solid #2196F3',
                              }}
                            >
                              <HomeIcon sx={{ fontSize: 40, color: '#2196F3' }} />
                            </Avatar>
                          </Box>


                          {/* Project Details */}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                              {project?.name || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {project?.developer || 'N/A'}
                            </Typography>
                            {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {project.type}
                          </Typography>
                          <Chip
                            label={project.type}
                            size="small"
                            sx={{
                              backgroundColor: '#536DFE',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '11px',
                              height: '22px',
                              mb: 1
                            }}
                          /> */}


                            {/* Contact Info */}
                            <Box sx={{ mt: 1.5 }}>
                              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 16, color: '#666' }} />
                                <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                  {project?.email || 'N/A'}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 16, color: '#25D366' }} />
                                <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                  {project?.phone || 'N/A'}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={1}>
                                <LocationIcon sx={{ fontSize: 16, color: '#666' }} />
                                <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                  {project?.city || 'N/A'}, {project?.state || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>


                      {/* Right Side: Tabs and Tab Content */}
                      <Grid item xs={12} md={7}>
                        <Box>
                          <Tabs
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            value={activeTab}
                            onChange={(e, newValue) => handleTabChange(project.projectId, newValue)}
                            sx={{
                              minHeight: 40,
                              borderBottom: '1px solid #e0e0e0',
                              '& .MuiTab-root': {
                                minHeight: 40,
                                textTransform: 'none',
                                fontSize: '13px',
                                fontWeight: 600,
                              }
                            }}
                          >
                            <Tab label="Project Info" />
                            <Tab label="Inventory" />
                            <Tab label="Contact" />
                            <Tab label="Address" />
                            <Tab label="Bank Details" />
                          </Tabs>


                          {/* Tab Content */}
                          <Box sx={{ mt: 2 }}>
                            {/* Project Info */}
                            {activeTab === 0 && (
                              <Box>
                                <Grid container spacing={1}>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        Start Date
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.startDate || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        Complete Date
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.completeDate || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        Contact Person
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.contactPerson || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        RERA No
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.reraNo || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}


                            {/* Inventory */}
                            {activeTab === 1 && (
                              <Box>
                                <Grid container spacing={1}>
                                  {(() => {
                                    const typeKey = String(project.type || '').toLowerCase();
                                    const typeConfig = typeInventoryConfig[typeKey];
                                    const baseItems = [
                                      typeConfig
                                        ? {
                                          key: typeConfig?.key,
                                          label: typeConfig?.label,
                                          value: project?.[typeConfig?.key] || 'N/A',
                                        }
                                        : null,
                                      {
                                        key: 'guidelineValue',
                                        label: 'Circle Rate',
                                        value: project?.guidelineValue || 'N/A',
                                      },
                                      {
                                        key: 'possessionDate',
                                        label: 'Possession Date',
                                        value: project?.possessionDate || 'N/A',
                                      },
                                      {
                                        key: 'address',
                                        label: 'Address',
                                        value: project?.address || 'N/A',
                                      },
                                    ].filter(Boolean);

                                    return baseItems.map((item) => (
                                      <Grid item xs={12} sm={6} md={3} key={`${project.projectId}-${item.key}`}>
                                        <Box sx={{ p: 1, borderRadius: 1 }}>
                                          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                            {item.label}
                                          </Typography>
                                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                            {item.value}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    ));
                                  })()}
                                </Grid>
                              </Box>
                            )}


                            {/* Contact */}
                            {activeTab === 2 && (
                              <Box>
                                <Grid container spacing={1}>
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        Contact Person
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.contactPerson || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        Email
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.email || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        Phone
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.phone || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}


                            {/* Address */}
                            {activeTab === 3 && (
                              <Box>
                                <Grid container spacing={3}>
                                  <Grid item xs={12} sm={4}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        Full Address
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.address || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        City
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.city || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        State
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.state || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}


                            {/* Bank Details */}
                            {activeTab === 4 && (
                              <Box>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 2, p: 1, borderRadius: 1 }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Bank Name
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {project?.bankName || 'N/A'}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Account Holder Name
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {project?.accountHolderName || 'N/A'}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                          Account Number
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                          {project?.accountNumber || 'N/A'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ p: 1, borderRadius: 1 }}>
                                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        IFSC Code
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '12px' }}>
                                        {project?.ifscCode || 'N/A'}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>


                    {/* Bottom Actions */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 2, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetails(project)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '12px',
                          px: 2,
                        }}
                      >
                        More Details
                      </Button>
                      {canManageProjects && (
                        <>
                          <Tooltip title='Edit'>
                            <IconButton
                              color="primary"
                              size="small"
                              sx={{ border: '1px solid #e0e0e0' }}
                              onClick={() => handleEdit(project)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Delete'>
                            <IconButton
                              color="error"
                              size="small"
                              sx={{ border: '1px solid #e0e0e0' }}
                              onClick={() => handleDelete(project._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      <Tooltip title={
                        project?.projectType === 'villa' ? 'View Villa List' :
                          project?.projectType === 'commercial' ? 'View Commercial List' :
                            project?.projectType === 'apartment' ? 'View Apartment List' :
                              project?.projectType === 'plot' ? 'View Plot List' : `View ${project.name}`
                      }>

                        <IconButton
                          size="small"
                          sx={{ border: '1px solid #e0e0e0', color: '#4caf50' }}
                          onClick={() => {
                            if (project?.badge?.toLowerCase() === 'plot') {
                              history.push({
                                pathname: '/app/project/PlotListDetails',
                                search: `?projectId=${project._id}`
                              });
                            } else if (project?.badge?.toLowerCase() === 'villa') {
                              history.push({
                                pathname: '/app/project/ProjectEnquiry',
                                search: `?projectId=${project._id}`
                              });
                            } else if (project?.badge?.toLowerCase() === 'commercial' || project?.badge?.toLowerCase() === 'apartment') {
                              history.push('/app/project/apartment/buildings');
                            }
                          }}
                        >
                          <ModuleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
      </Grid>


      {/* Pagination */}
      {
        displayData.length > 0 && (
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <TablePagination
              // rowsPerPageOptions={[5, 10, 15, 20]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={isMobile ? 'Rows:' : 'Rows per page:'}
            />
          </Card>
        )
      }
      {/* Modals */}
      <AlertModal
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />

      <ConfirmModal
        open={confirmConfig.open}
        title="Delete Project"
        onClose={() => setConfirmConfig({ ...confirmConfig, open: false })}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
      >
        Are you sure you want to delete this project? This action cannot be undone.
      </ConfirmModal>
    </Box >
  );
};


export default ProjectList;
